import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInstantTheme } from '../../contexts/InstantThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLayout } from '../../contexts/LayoutContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Euro,
  Restaurant,
  Assessment,
  People,
  Dashboard,
  Receipt,
  Payment,
  Assessment as ReportsIcon,
  Logout,
  FilterList,
  FileDownload,
  Refresh,
  Close,
} from '@mui/icons-material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';

interface ReportData {
  summary: {
  totalRevenue: number;
  totalOrders: number;
    totalCustomers: number;
    averageOrderValue: number;
    splitPaymentOrders: number;
    splitPaymentRevenue: number;
    splitPaymentPercentage: number;
    totalDiscounts: number;
  };
  revenueByDay: { date: string; revenue: number }[];
  ordersByHour: { hour: string; orders: number }[];
  topItems: { name: string; quantity: number; revenue: number }[];
  paymentMethods: { method: string; count: number; percentage: number }[];
  salesByHour: { hour: string; revenue: number }[];
}

const ModernReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { appTheme } = useInstantTheme();
  const { t, language } = useLanguage();
  const { hasSharedSidebar } = useLayout();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter and Export states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: '7days',
    startDate: '',
    endDate: '',
    orderStatus: 'all',
    paymentMethod: 'all',
    branch: 'all'
  });
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportLanguage, setExportLanguage] = useState<'en' | 'ar'>(language as 'en' | 'ar');

  // Helper functions to generate reports data from orders
  const generateRevenueByDay = (orders: any[]) => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last7Days.map(date => {
      const dayOrders = orders.filter(order => {
        if (!order.created_at) return false;
        // Handle both string and Timestamp formats
        const orderDate = typeof order.created_at === 'string' 
          ? order.created_at 
          : (order.created_at.toDate ? order.created_at.toDate().toISOString() : new Date(order.created_at).toISOString());
        return orderDate.startsWith(date);
      });
      const revenue = dayOrders.reduce((sum, order) => sum + (order.total || 0), 0);
      return {
        date: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
        revenue: parseFloat(revenue.toFixed(2))
      };
    });
  };

  const generateOrdersByHour = (orders: any[]) => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    return hours.map(hour => {
      const hourOrders = orders.filter(order => {
        if (!order.created_at) return false;
        const orderHour = new Date(order.created_at).getHours();
        return orderHour === hour;
      });
      return {
        hour: `${hour}:00`,
        orders: hourOrders.length
      };
    });
  };

  const generateTopItems = (orders: any[]) => {
    const itemCounts: { [key: string]: { quantity: number; revenue: number } } = {};
    
    orders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach((item: any) => {
          const itemName = item.name || item.item_name || 'Unknown Item';
          if (!itemCounts[itemName]) {
            itemCounts[itemName] = { quantity: 0, revenue: 0 };
          }
          itemCounts[itemName].quantity += item.quantity || 1;
          itemCounts[itemName].revenue += item.total || ((item.price || 0) * (item.quantity || 1));
        });
      }
    });

    return Object.entries(itemCounts)
      .map(([name, data]) => ({
        name,
        quantity: data.quantity,
        revenue: parseFloat(data.revenue.toFixed(2))
      }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);
  };

  // Helper: process raw orders into ReportData
  const processOrdersToReport = useCallback((ordersData: any[]): ReportData => {
    const validOrders = ordersData.filter((order: any) => {
      const status = (order.status || '').toLowerCase();
      const paymentStatus = (order.payment_status || order.paymentStatus || '').toLowerCase();
      if (status === 'cancelled') return false;
      // Only include completed/delivered orders (consistent with Payments page)
      return status === 'completed' || status === 'delivered' || paymentStatus === 'completed';
    });

    const totalRevenue = validOrders.reduce((sum: number, order: any) => {
      const orderTotal = typeof order.total === 'number' ? order.total : parseFloat(order.total || '0');
      return sum + (isNaN(orderTotal) ? 0 : orderTotal);
    }, 0);
    const totalOrders = validOrders.length;
    const totalCustomers = new Set(validOrders.map((o: any) => o.customer_name).filter(Boolean)).size;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    const splitPaymentOrders = validOrders.filter((o: any) => o.is_split_payment).length;
    const splitPaymentRevenue = validOrders
      .filter((o: any) => o.is_split_payment)
      .reduce((sum: number, o: any) => {
        const t = typeof o.total === 'number' ? o.total : parseFloat(o.total || '0');
        return sum + (isNaN(t) ? 0 : t);
      }, 0);
    const splitPaymentPercentage = totalOrders > 0 ? (splitPaymentOrders / totalOrders) * 100 : 0;

    const totalDiscounts = validOrders.reduce((sum: number, order: any) => {
      const disc = typeof order.discount_amount === 'number' ? order.discount_amount : parseFloat(order.discount_amount || '0');
      return sum + (isNaN(disc) ? 0 : disc);
    }, 0);

    const revenueByDay = generateRevenueByDay(validOrders);
    const ordersByHour = generateOrdersByHour(validOrders);
    const topItems = generateTopItems(validOrders);

    // Sales by hour (revenue)
    const salesByHourMap = new Map<string, number>();
    validOrders.forEach((order: any) => {
      if (order.created_at) {
        const orderDate = new Date(typeof order.created_at === 'string' ? order.created_at : order.created_at);
        if (!isNaN(orderDate.getTime())) {
          const hour = orderDate.getHours();
          const hourKey = `${hour.toString().padStart(2, '0')}:00`;
          const orderTotal = typeof order.total === 'number' ? order.total : parseFloat(order.total || '0');
          salesByHourMap.set(hourKey, (salesByHourMap.get(hourKey) || 0) + (isNaN(orderTotal) ? 0 : orderTotal));
        }
      }
    });
    const salesByHour = Array.from({ length: 24 }, (_, i) => {
      const hourKey = `${i.toString().padStart(2, '0')}:00`;
      return { hour: hourKey, revenue: parseFloat((salesByHourMap.get(hourKey) || 0).toFixed(2)) };
    });

    // Real payment methods from order data
    const paymentMethodCounts = new Map<string, number>();
    validOrders.forEach((order: any) => {
      const method = order.payment_method || order.paymentMethod || 'cash';
      const methodName = method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
      paymentMethodCounts.set(methodName, (paymentMethodCounts.get(methodName) || 0) + 1);
    });
    const paymentMethods = Array.from(paymentMethodCounts.entries())
      .map(([method, count]) => ({
        method,
        count,
        percentage: totalOrders > 0 ? parseFloat(((count / totalOrders) * 100).toFixed(1)) : 0
      }))
      .sort((a, b) => b.count - a.count);

    return {
      summary: {
        totalRevenue: parseFloat(totalRevenue.toFixed(2)),
        totalOrders,
        totalCustomers,
        averageOrderValue: parseFloat(averageOrderValue.toFixed(2)),
        splitPaymentOrders,
        splitPaymentRevenue: parseFloat(splitPaymentRevenue.toFixed(2)),
        splitPaymentPercentage: parseFloat(splitPaymentPercentage.toFixed(2)),
        totalDiscounts: parseFloat(totalDiscounts.toFixed(2))
      },
      revenueByDay,
      ordersByHour,
      topItems,
      paymentMethods: paymentMethods.length > 0 ? paymentMethods : [
        { method: 'Cash', count: 0, percentage: 0 },
        { method: 'Card', count: 0, percentage: 0 },
        { method: 'PayPal', count: 0, percentage: 0 }
      ],
      salesByHour,
    };
  }, []);

  // Map a Supabase orders row (+ joined items) to the flat shape this page expects.
  const mapSupabaseOrder = (row: any): any => {
    const items = (row.order_items || []).map((it: any) => ({
      name: it.menu_item_snapshot?.name || 'Item',
      quantity: it.quantity || 1,
      price: Number(it.unit_price) || 0,
      total: Number(it.line_total) || 0,
    }));
    return {
      id: row.id,
      order_number: row.order_number || row.id,
      customer_name: row.customer_snapshot?.name || 'Walk-in Customer',
      items,
      total: Number(row.grand_total) || 0,
      status: row.status || 'pending',
      created_at: row.created_at,
      notes: row.notes || '',
      created_by: row.created_by || 'Unknown',
      order_type: row.type || 'dine_in',
      table_number: row.customer_snapshot?.table_number || null,
      payment_method: row.payment_method || 'cash',
      is_split_payment: false,
      split_session_id: null,
      split_person_name: null,
      split_total_people: null,
      split_person_index: null,
    };
  };

  // Manual refresh handler
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { supabase } = await import('../../services/supabaseClient');
      const { data, error: err } = await supabase
        .from('orders')
        .select('id, order_number, customer_snapshot, grand_total, status, type, notes, payment_method, created_at, order_items ( menu_item_snapshot, quantity, unit_price, line_total )')
        .order('created_at', { ascending: false })
        .limit(500);
      if (err) throw err;

      const ordersData = (data || []).map(mapSupabaseOrder);
      console.log('📊 [REPORTS] Loaded', ordersData.length, 'orders for reports');
      setReportData(processOrdersToReport(ordersData));
    } catch (error) {
      console.error('Failed to load reports:', error);
      setError('Failed to load reports data.');
    } finally {
      setLoading(false);
    }
  }, [processOrdersToReport]);

  useEffect(() => {
    let cancelled = false;
    let channel: any = null;

    const setup = async () => {
      const { supabase } = await import('../../services/supabaseClient');
      const reload = async () => {
        if (cancelled) return;
        const { data } = await supabase
          .from('orders')
          .select('id, order_number, customer_snapshot, grand_total, status, type, notes, payment_method, created_at, order_items ( menu_item_snapshot, quantity, unit_price, line_total )')
          .order('created_at', { ascending: false })
          .limit(500);
        if (cancelled) return;
        const ordersData = (data || []).map(mapSupabaseOrder);
        setReportData(processOrdersToReport(ordersData));
        setLoading(false);
        setError(null);
      };
      await reload();
      channel = supabase
        .channel(`reports_${Date.now()}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => { void reload(); })
        .subscribe();
    };

    setup().catch((err) => {
      console.error('[REPORTS] setup failed:', err);
      fetchReports();
    });

    return () => {
      cancelled = true;
      if (channel) {
        import('../../services/supabaseClient').then(({ supabase }) => supabase.removeChannel(channel));
      }
    };
  }, [fetchReports, processOrdersToReport]);

  const navigationItems = [
    { id: 'dashboard', label: t('nav.dashboard'), icon: Dashboard, path: '/dashboard', active: false },
    { id: 'orders', label: t('nav.orders'), icon: Receipt, path: '/orders', active: false },
    { id: 'payments', label: t('nav.payments'), icon: Payment, path: '/payments', active: false },
    { id: 'reports', label: t('nav.reports'), icon: ReportsIcon, path: '/reports', active: true },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem('pos_token');
    localStorage.removeItem('pos_user');
    navigate('/login');
  };

  // Professional Report Generation Functions
  const loadLogoAsBase64 = async (): Promise<string> => {
    try {
      // Try loading from local file first (same origin, no CORS)
      const localResponse = await fetch('/saheeh_logo_w.svg', { cache: 'no-cache' });
      if (localResponse.ok) {
        const blob = await localResponse.blob();
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (reader.result && typeof reader.result === 'string') {
              resolve(reader.result);
            } else {
              reject(new Error('Invalid logo data'));
            }
          };
          reader.onerror = () => reject(new Error('FileReader error'));
          reader.readAsDataURL(blob);
        });
        console.log('✅ [REPORTS] Logo loaded from local file');
        return base64;
      }
    } catch (error) {
      console.warn('⚠️ [REPORTS] Local logo failed, trying Firebase Storage:', error);
    }
    
    // Fallback: Try Firebase Storage via LogoCacheService
    try {
      const LogoCacheService = (await import('../../services/logoCacheService')).default;
      const logoData = await LogoCacheService.getLogo();
      if (logoData) {
        console.log('✅ [REPORTS] Logo loaded from LogoCacheService');
        return logoData;
      }
    } catch (error) {
      console.error('❌ [REPORTS] LogoCacheService failed:', error);
    }
    
    // Final fallback: Return empty string (will show alt text)
    console.warn('⚠️ [REPORTS] All logo loading methods failed, using empty string');
    return '';
  };

  const generateTSECompliantReport = async (data: ReportData, lang: string, logoBase64: string = '') => {
    const translations = {
      en: {
        title: 'SAHEEH SUSHI & BOWLS - Business Report',
        branch: 'Saheeh Hürth',
        address: 'Sudetenstraße 60, 50354 Hürth',
        phone: 'Tel.: 022337063324',
        website: 'www.saheehpos.com',
        reportDate: 'Report Date',
        period: 'Reporting Period',
        summary: 'Summary',
        totalRevenue: 'Total Revenue',
        totalOrders: 'Total Orders',
        totalCustomers: 'Total Customers',
        averageOrderValue: 'Average Order Value',
        revenueByDay: 'Revenue by Day',
        topItems: 'Top Selling Items',
        paymentMethods: 'Payment Methods',
        salesByHour: 'Sales by Hour',
        tseCompliant: 'TSE Compliant Report',
        generatedAt: 'Generated at',
        reportId: 'Report ID',
        vatIncluded: 'All prices include 19% VAT',
        thankYou: 'Thank you for your business!'
      },
      de: {
        title: 'SAHEEH SUSHI & BOWLS - Geschäftsbericht',
        branch: 'Saheeh Hürth',
        address: 'Sudetenstraße 60, 50354 Hürth',
        phone: 'Tel.: 022337063324',
        website: 'www.saheehpos.com',
        reportDate: 'Berichtsdatum',
        period: 'Berichtszeitraum',
        summary: 'Zusammenfassung',
        totalRevenue: 'Gesamtumsatz',
        totalOrders: 'Gesamtbestellungen',
        totalCustomers: 'Gesamtkunden',
        averageOrderValue: 'Durchschnittlicher Bestellwert',
        revenueByDay: 'Umsatz nach Tag',
        topItems: 'Bestseller',
        paymentMethods: 'Zahlungsmethoden',
        salesByHour: 'Umsatz nach Stunde',
        tseCompliant: 'TSE-konformer Bericht',
        generatedAt: 'Erstellt am',
        reportId: 'Berichts-ID',
        vatIncluded: 'Alle Preise inkl. 19% MwSt.',
        thankYou: 'Vielen Dank für Ihr Geschäft!'
      },
      nl: {
        title: 'SAHEEH SUSHI & BOWLS - Bedrijfsrapport',
        branch: 'Saheeh Hürth',
        address: 'Sudetenstraße 60, 50354 Hürth',
        phone: 'Tel.: 022337063324',
        website: 'www.saheehpos.com',
        reportDate: 'Rapportdatum',
        period: 'Rapportageperiode',
        summary: 'Samenvatting',
        totalRevenue: 'Totale Omzet',
        totalOrders: 'Totale Bestellingen',
        totalCustomers: 'Totale Klanten',
        averageOrderValue: 'Gemiddelde Bestelwaarde',
        revenueByDay: 'Omzet per Dag',
        topItems: 'Bestsellers',
        paymentMethods: 'Betaalmethoden',
        salesByHour: 'Omzet per Uur',
        tseCompliant: 'TSE-conform Rapport',
        generatedAt: 'Gegenereerd op',
        reportId: 'Rapport-ID',
        vatIncluded: 'Alle prijzen incl. 19% BTW',
        thankYou: 'Bedankt voor uw zaken!'
      }
    };

    const t = translations[lang as keyof typeof translations] || translations.en;
    const reportId = `RPT-${Date.now().toString(36).toUpperCase()}`;
    const generatedAt = new Date().toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US');

    return `
      <!DOCTYPE html>
      <html lang="${lang}">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${t.title}</title>
        <style>
          body {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 20px;
            background: white;
            color: #000;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            border-bottom: 3px solid #000;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .logo {
            height: 100px;
            margin-bottom: 20px;
            display: block;
            margin-left: auto;
            margin-right: auto;
          }
          .company-info {
            font-size: 14px;
            color: #666;
            margin: 10px 0;
          }
          .report-meta {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            font-size: 12px;
            color: #666;
          }
          .section {
            margin-bottom: 30px;
          }
          .section-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 15px;
            border-bottom: 1px solid #ccc;
            padding-bottom: 5px;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 15px;
            margin-bottom: 20px;
          }
          .summary-card {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            text-align: center;
          }
          .summary-value {
            font-size: 24px;
            font-weight: bold;
            color: #1976D2;
          }
          .summary-label {
            font-size: 12px;
            color: #666;
            margin-top: 5px;
          }
          .table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .table th, .table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          .table th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 12px;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 20px;
          }
          .tse-info {
            background: #e8f5e8;
            padding: 10px;
            border-radius: 5px;
            margin: 20px 0;
            font-size: 12px;
          }
          @media print {
            body { margin: 0; padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${logoBase64 ? `<img src="${logoBase64}" alt="SAHEEH Logo" class="logo">` : '<div class="logo-placeholder" style="height: 100px; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: bold; color: #1976D2;">SAHEEH</div>'}
          <h2 style="text-align: center; margin-top: 15px; margin-bottom: 10px; color: #333; font-size: 20px; font-weight: bold;">
            ${lang === 'ar' ? 'GESCHÄFTSBERICHT' : 'BUSINESS REPORT'}
          </h2>
          <div class="company-info">
            <div>${t.branch}</div>
            <div>${t.address}</div>
            <div>${t.phone}</div>
            <div>${t.website}</div>
          </div>
        </div>

        <div class="report-meta">
          <div>
            <strong>${t.reportId}:</strong> ${reportId}<br>
            <strong>${t.generatedAt}:</strong> ${generatedAt}
          </div>
          <div>
            <strong>${t.period}:</strong> ${filters.dateRange === '7days' ? 'Last 7 Days' : 
              filters.dateRange === '30days' ? 'Last 30 Days' : 
              filters.dateRange === 'custom' ? `${filters.startDate} - ${filters.endDate}` : 'All Time'}
          </div>
        </div>

        <div class="tse-info">
          <strong>${t.tseCompliant}</strong> - ${t.vatIncluded}
        </div>

        <div class="section">
          <div class="section-title">${t.summary}</div>
          <div class="summary-grid">
            <div class="summary-card">
              <div class="summary-value">€${data.summary.totalRevenue.toFixed(2)}</div>
              <div class="summary-label">${t.totalRevenue}</div>
            </div>
            <div class="summary-card">
              <div class="summary-value">${data.summary.totalOrders}</div>
              <div class="summary-label">${t.totalOrders}</div>
            </div>
            <div class="summary-card">
              <div class="summary-value">${data.summary.totalCustomers}</div>
              <div class="summary-label">${t.totalCustomers}</div>
            </div>
            <div class="summary-card">
              <div class="summary-value">€${data.summary.averageOrderValue.toFixed(2)}</div>
              <div class="summary-label">${t.averageOrderValue}</div>
            </div>
            <div class="summary-card" style="border: 2px solid #2196F3;">
              <div class="summary-value" style="color: #2196F3;">${data.summary.splitPaymentOrders || 0}</div>
              <div class="summary-label">${lang === 'ar' ? 'Geteilte Zahlungen' : 'Split Payments'}</div>
              <div style="font-size: 0.85rem; color: #666; margin-top: 8px;">
                €${(data.summary.splitPaymentRevenue || 0).toFixed(2)} (${(data.summary.splitPaymentPercentage || 0).toFixed(1)}%)
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">${t.revenueByDay}</div>
          <table class="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              ${data.revenueByDay.map(day => `
                <tr>
                  <td>${day.date}</td>
                  <td>€${day.revenue.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">${t.topItems}</div>
          <table class="table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              ${data.topItems.map(item => `
                <tr>
                  <td>${item.name}</td>
                  <td>${item.quantity}</td>
                  <td>€${item.revenue.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">${t.paymentMethods}</div>
          <table class="table">
            <thead>
              <tr>
                <th>Method</th>
                <th>Count</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              ${data.paymentMethods.map(method => `
                <tr>
                  <td>${method.method}</td>
                  <td>${method.count}</td>
                  <td>${method.percentage.toFixed(1)}%</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="footer">
          <div>${t.thankYou}</div>
          <div>${t.website} | ${t.phone}</div>
          <div>${t.tseCompliant} - ${t.vatIncluded}</div>
        </div>
      </body>
      </html>
    `;
  };

  const handleFilter = () => {
    setFilterDialogOpen(true);
  };

  const handleExport = () => {
    setExportDialogOpen(true);
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchReports();
  };

  const applyFilters = () => {
    setFilterDialogOpen(false);
    setLoading(true);
    fetchReports();
  };

  const generateExport = async () => {
    if (!reportData) {
      console.error('❌ [EXPORT] No report data available');
      alert('No report data available. Please refresh the page and try again.');
      return;
    }

    console.log('📊 [EXPORT] Generating export...', { format: exportFormat, language: exportLanguage });
    
    try {
      // Load logo as base64 before generating HTML
      const logoBase64 = await loadLogoAsBase64();
      const reportHTML = await generateTSECompliantReport(reportData, exportLanguage, logoBase64);
      
      if (exportFormat === 'pdf') {
        // Open in new window for printing/PDF generation
        console.log('📄 [EXPORT] Opening print window for PDF...');
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (printWindow) {
          printWindow.document.write(reportHTML);
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
          }, 1000);
          console.log('✅ [EXPORT] Print window opened successfully');
        } else {
          console.error('❌ [EXPORT] Failed to open print window - popup blocked?');
          alert('Failed to open print window. Please allow popups for this site and try again.');
        }
      } else if (exportFormat === 'html') {
        // Download as HTML file
        console.log('📄 [EXPORT] Generating HTML download...');
        const blob = new Blob([reportHTML], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `saheeh-report-${Date.now()}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        console.log('✅ [EXPORT] HTML file downloaded successfully');
      } else if (exportFormat === 'csv') {
        // Generate CSV data
        console.log('📊 [EXPORT] Generating CSV download...');
        const csvData = generateCSVReport(reportData);
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `saheeh-report-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        console.log('✅ [EXPORT] CSV file downloaded successfully');
      }
      
      setExportDialogOpen(false);
      console.log('✅ [EXPORT] Export completed successfully');
    } catch (error) {
      console.error('❌ [EXPORT] Export failed:', error);
      alert(`Failed to generate export: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const generateCSVReport = (data: ReportData) => {
    const translations = {
      en: { totalRevenue: 'Total Revenue', totalOrders: 'Total Orders', totalCustomers: 'Total Customers', averageOrderValue: 'Average Order Value' },
      de: { totalRevenue: 'Gesamtumsatz', totalOrders: 'Gesamtbestellungen', totalCustomers: 'Gesamtkunden', averageOrderValue: 'Durchschnittlicher Bestellwert' },
      nl: { totalRevenue: 'Totale Omzet', totalOrders: 'Totale Bestellingen', totalCustomers: 'Totale Klanten', averageOrderValue: 'Gemiddelde Bestelwaarde' }
    };
    
    const t = translations[exportLanguage as keyof typeof translations] || translations.en;
    
    let csv = `SAHEEH SUSHI & BOWLS - Business Report\n`;
    csv += `Report ID,RPT-${Date.now().toString(36).toUpperCase()}\n`;
    csv += `Generated,${new Date().toLocaleString()}\n\n`;
    
    csv += `Summary\n`;
    csv += `${t.totalRevenue},€${data.summary.totalRevenue.toFixed(2)}\n`;
    csv += `${t.totalOrders},${data.summary.totalOrders}\n`;
    csv += `${t.totalCustomers},${data.summary.totalCustomers}\n`;
    csv += `${t.averageOrderValue},€${data.summary.averageOrderValue.toFixed(2)}\n\n`;
    
    csv += `Revenue by Day\n`;
    csv += `Date,Revenue\n`;
    data.revenueByDay.forEach(day => {
      csv += `${day.date},€${day.revenue.toFixed(2)}\n`;
    });
    
    csv += `\nTop Items\n`;
    csv += `Item,Quantity,Revenue\n`;
    data.topItems.forEach(item => {
      csv += `${item.name},${item.quantity},€${item.revenue.toFixed(2)}\n`;
    });
    
    return csv;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No report data available</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: hasSharedSidebar ? 'block' : 'flex',
      width: '100%',
      minHeight: '100vh',
      backgroundColor: appTheme.theme.background_color
    }}>
      {/* Fixed Sidebar - Only show if NOT in SharedLayout */}
      {!hasSharedSidebar && (
      <Box sx={{
        width: 280,
        backgroundColor: appTheme.theme.background_color || '#ffffff',
        borderRight: '1px solid #e9ecef',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'relative',
        height: '100vh'
      }}>

        {/* Logo Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e9ecef' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 180,
              height: 180,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}>
              <img
                src="/saheeh_logo_w.svg"
                alt="Saheeh"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain' 
                }} 
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#212529' }}>
                Saheeh POS
              </Typography>
              <Typography variant="body2" sx={{ color: '#6c757d' }}>
                Berlin Mitte
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Navigation Menu */}
        <Box sx={{ flex: 1, p: 2 }}>
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            return (
            <Button
                key={item.id}
              fullWidth
                startIcon={<IconComponent sx={{ fontSize: '1.2rem' }} />}
                onClick={() => handleNavigation(item.path)}
              sx={{
                justifyContent: 'flex-start',
                mb: 1,
                  py: 1.5,
                  px: 2,
                borderRadius: '8px',
                textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: item.active ? '#ffffff' : '#495057',
                  backgroundColor: item.active ? (appTheme.theme.navigation_sidebar_active_color || '#FF8E53') : (appTheme.theme.navigation_sidebar_inactive_color || '#f8f9fa'),
                  border: item.active ? 'none' : '1px solid #e9ecef',
                '&:hover': {
                    backgroundColor: item.active 
                      ? (appTheme.theme.navigation_sidebar_active_color || '#FF8E53')
                      : (appTheme.theme.navigation_sidebar_hover_color || '#e9ecef'),
                    color: item.active ? '#ffffff' : '#495057',
                    transform: 'translateX(4px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  },
                  transition: 'all 0.2s ease-in-out',
              }}
            >
              {item.label}
            </Button>
            );
          })}
        </Box>

        {/* Logout Button */}
        <Box sx={{ p: 2, borderTop: '1px solid #e9ecef' }}>
          <Button
            fullWidth
            startIcon={<Logout sx={{ fontSize: '1.2rem' }} />}
            onClick={handleLogout}
            sx={{
              justifyContent: 'flex-start',
              py: 1.5,
              px: 2,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#dc3545',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              '&:hover': {
                backgroundColor: '#dc3545',
                color: '#ffffff',
                transform: 'translateX(4px)',
                boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Logout
          </Button>
        </Box>
      </Box>
      )}

      {/* Main Content Area - Scrollable */}
      <Box sx={{
        flex: hasSharedSidebar ? undefined : 1,
        width: hasSharedSidebar ? '100%' : 'auto',
        overflow: 'auto',
        height: '100vh',
        backgroundColor: appTheme.theme.background_color
      }} className="main-content-area">
        <Box sx={{ p: { xs: 1.5, md: 3 }, pb: { xs: 4, md: 3 } }}>
        {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: appTheme.theme.text_color, mb: 1 }}>
                  {t('reports.title')}
                </Typography>
                <Typography variant="body1" sx={{ color: '#666666' }}>
                  {t('reports.subtitle')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* User Info Display */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  px: 2, 
                  py: 1, 
                  backgroundColor: appTheme.theme.background_color,
                  borderRadius: 2,
                  border: `1px solid ${appTheme.theme.primary_color}20`
                }}>
                  <Assessment sx={{ color: appTheme.theme.primary_color, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: appTheme.theme.text_color, fontWeight: 500 }}>
                    {(() => { try { const u = JSON.parse(localStorage.getItem('saheeh_pos_user') || '{}'); const r = u.role || 'staff'; return `${u.email || 'Unknown'} (${r.charAt(0).toUpperCase() + r.slice(1)})`; } catch { return 'Unknown (Staff)'; } })()}
                  </Typography>
                </Box>
                
                {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Filter Button - Redesigned */}
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={handleFilter}
                  sx={{
                    borderColor: appTheme.theme.primary_color || '#1976D2',
                    color: appTheme.theme.primary_color || '#1976D2',
                    borderWidth: '2px',
                    borderRadius: '10px',
                    px: 2.5,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    minWidth: 120,
                    backgroundColor: `${appTheme.theme.primary_color || '#1976D2'}08`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    '&:hover': {
                      borderColor: appTheme.theme.primary_color || '#1565C0',
                      backgroundColor: `${appTheme.theme.primary_color || '#1976D2'}15`,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {t('reports.filter')}
                </Button>

                {/* Export Button - Redesigned */}
                <Button
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={handleExport}
                  sx={{
                    borderColor: '#4CAF50',
                    color: '#4CAF50',
                    borderWidth: '2px',
                    borderRadius: '10px',
                    px: 2.5,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    minWidth: 120,
                    backgroundColor: '#E8F5E9',
                    boxShadow: '0 2px 4px rgba(76, 175, 80, 0.1)',
                    '&:hover': {
                      borderColor: '#388E3C',
                      backgroundColor: '#C8E6C9',
                      color: '#2E7D32',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {t('reports.export')}
                </Button>

                {/* Refresh Button - Redesigned */}
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    backgroundColor: appTheme.theme.primary_color || '#1976D2',
                    color: 'white',
                    borderRadius: '10px',
                    px: 2.5,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    minWidth: 120,
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      backgroundColor: appTheme.theme.primary_color || '#1565C0',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(25, 118, 210, 0.4)',
                    },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                      transform: 'none',
                      boxShadow: 'none',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {t('reports.refresh')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

          {/* Summary Cards - All in one row with consistent design */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(5, 1fr)' 
            },
            gap: 2,
            mb: 4
          }}>
            {/* Total Revenue */}
            <Box>
              <Card sx={{ 
                height: '100%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: appTheme.theme.kpi_card_background || '#ffffff',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minHeight: 80 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.75rem',
                        color: appTheme.theme.kpi_card_text_color || '#2c3e50',
                        mb: 0.5,
                        lineHeight: 1.2
                      }}>
                        €{reportData.summary.totalRevenue.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: '#666666',
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}>
                        {t('reports.total_revenue')}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Euro sx={{ fontSize: 32, color: appTheme.theme.kpi_icon_color_1 || '#1976d2' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Total Orders */}
            <Box>
              <Card sx={{ 
                height: '100%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: appTheme.theme.kpi_card_background || '#ffffff',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minHeight: 80 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.75rem',
                        color: appTheme.theme.kpi_card_text_color || '#2c3e50',
                        mb: 0.5,
                        lineHeight: 1.2
                      }}>
                        {reportData.summary.totalOrders}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: '#666666',
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}>
                        {t('reports.total_orders')}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      backgroundColor: '#e8f5e9', 
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Restaurant sx={{ fontSize: 32, color: appTheme.theme.kpi_icon_color_2 || '#4caf50' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            
            {/* Split Payments */}
            <Box>
              <Card sx={{ 
                height: '100%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: appTheme.theme.kpi_card_background || '#ffffff',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minHeight: 80 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.75rem',
                        color: appTheme.theme.kpi_card_text_color || '#2c3e50',
                        mb: 0.5,
                        lineHeight: 1.2
                      }}>
                        {reportData.summary.splitPaymentOrders || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: '#666666',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        mb: 0.25
                      }}>
                        {t('reports.split_payments')}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        color: '#999', 
                        display: 'block',
                        fontSize: '0.75rem'
                      }}>
                        €{(reportData.summary.splitPaymentRevenue || 0).toFixed(2)} ({(reportData.summary.splitPaymentPercentage || 0).toFixed(1)}%)
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      backgroundColor: '#fff3e0', 
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Payment sx={{ fontSize: 32, color: '#ff9800' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Total Customers */}
            <Box>
              <Card sx={{ 
                height: '100%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: appTheme.theme.kpi_card_background || '#ffffff',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minHeight: 80 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.75rem',
                        color: appTheme.theme.kpi_card_text_color || '#2c3e50',
                        mb: 0.5,
                        lineHeight: 1.2
                      }}>
                        {reportData.summary.totalCustomers}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: '#666666',
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}>
                        {t('reports.total_customers')}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      backgroundColor: '#f3e5f5', 
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <People sx={{ fontSize: 32, color: appTheme.theme.kpi_icon_color_3 || '#9c27b0' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Average Order Value */}
            <Box>
              <Card sx={{ 
                height: '100%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: appTheme.theme.kpi_card_background || '#ffffff',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minHeight: 80 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.75rem',
                        color: appTheme.theme.kpi_card_text_color || '#2c3e50',
                        mb: 0.5,
                        lineHeight: 1.2
                      }}>
                        €{reportData.summary.averageOrderValue.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: '#666666',
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}>
                        {t('reports.average_order_value')}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      backgroundColor: '#fce4ec', 
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Assessment sx={{ fontSize: 32, color: appTheme.theme.kpi_icon_color_4 || '#e91e63' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Total Discounts */}
            {reportData.summary.totalDiscounts > 0 && (
            <Box>
              <Card sx={{
                height: '100%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                backgroundColor: appTheme.theme.kpi_card_background || '#ffffff',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minHeight: 80 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{
                        fontWeight: 700,
                        fontSize: '1.75rem',
                        color: '#e65100',
                        mb: 0.5,
                        lineHeight: 1.2
                      }}>
                        -{'\u20AC'}{reportData.summary.totalDiscounts.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: '#666666',
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}>
                        {language === 'ar' ? 'Rabatte gesamt' : 'Total Discounts'}
                      </Typography>
                    </Box>
                    <Box sx={{
                      backgroundColor: '#fff3e0',
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Euro sx={{ fontSize: 32, color: '#e65100' }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            )}
          </Box>

          {/* Charts Grid */}
        <Grid container spacing={3}>
            {/* Revenue Trend */}
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('reports.revenue_trend')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={reportData.revenueByDay}>
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={appTheme.theme.chart_primary_color || '#4CAF50'} stopOpacity={0.8}/>
                          <stop offset="95%" stopColor={appTheme.theme.chart_primary_color || '#4CAF50'} stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <RechartsTooltip formatter={(value: any) => [`€${value}`, 'Revenue']} />
                      <Area type="monotone" dataKey="revenue" stroke={(() => {
                        const color = appTheme.theme.chart_primary_color || '#4CAF50';
                        console.log('🎨 Revenue Trend chart color:', color);
                        return color;
                      })()} fillOpacity={1} fill="url(#revenueGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Payment Methods */}
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('reports.payment_methods')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={220}>
                    <RechartsPieChart>
                      <defs>
                        <linearGradient id="paypalGradient" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor={appTheme.theme.chart_primary_color || '#0070ba'}/>
                          <stop offset="100%" stopColor={appTheme.theme.chart_primary_color || '#009cde'}/>
                        </linearGradient>
                        <linearGradient id="cashGradient" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor={appTheme.theme.chart_quaternary_color || '#ffc107'}/>
                          <stop offset="100%" stopColor={appTheme.theme.chart_quaternary_color || '#ff8f00'}/>
                        </linearGradient>
                        <linearGradient id="cardGradient" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor={appTheme.theme.chart_tertiary_color || '#6c757d'}/>
                          <stop offset="100%" stopColor={appTheme.theme.chart_tertiary_color || '#495057'}/>
                        </linearGradient>
                      </defs>
                      <Pie
                        data={reportData.paymentMethods}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={80}
                        innerRadius={30}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {reportData.paymentMethods.map((method, index) => {
                          const gradientId = method.method.toLowerCase().includes('card') ? 'cardGradient' :
                                           method.method.toLowerCase().includes('paypal') ? 'paypalGradient' : 'cashGradient';
                          return <Cell key={`cell-${index}`} fill={`url(#${gradientId})`} />;
                        })}
                      </Pie>
                      <RechartsTooltip formatter={(value: any, _name: any, props: any) => [`${props.payload.percentage}% (${value})`, props.payload.method]} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                  {/* Legend below chart */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                    {reportData.paymentMethods.map((method) => {
                      const color = method.method.toLowerCase().includes('card') ? (appTheme.theme.chart_tertiary_color || '#6c757d') :
                                   method.method.toLowerCase().includes('paypal') ? (appTheme.theme.chart_primary_color || '#0070ba') :
                                   (appTheme.theme.chart_quaternary_color || '#ffc107');
                      return (
                        <Box key={method.method} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
                          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#495057', fontWeight: 500 }}>
                            {method.method}: {method.percentage}%
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Top Items */}
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('reports.top_selling_items')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsBarChart data={reportData.topItems}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip formatter={(value: any) => [`${value}`, 'Quantity']} />
                      <Bar dataKey="quantity" fill={(() => {
                        const color = appTheme.theme.chart_tertiary_color || '#007bff';
                        console.log('🎨 Top Selling Items chart color:', color);
                        return color;
                      })()} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Sales by Hour */}
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('reports.sales_by_hour')}
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsBarChart data={reportData.salesByHour}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <RechartsTooltip formatter={(value: any) => [`€${value}`, 'Revenue']} />
                      <Bar dataKey="revenue" fill={(() => {
                        const color = appTheme.theme.chart_secondary_color || '#28a745';
                        console.log('🎨 Sales by Hour chart color:', color);
                        return color;
                      })()} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
      </Box>

      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('reports.filter_options')}
          </Typography>
          <IconButton onClick={() => setFilterDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('reports.date_range')}</InputLabel>
                <Select
                  value={filters.dateRange}
                  onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                  label={t('reports.date_range')}
                >
                  <MenuItem value="7days">{t('reports.last_7_days')}</MenuItem>
                  <MenuItem value="30days">{t('reports.last_30_days')}</MenuItem>
                  <MenuItem value="90days">{t('reports.last_90_days')}</MenuItem>
                  <MenuItem value="custom">{t('reports.custom_range')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('reports.order_status')}</InputLabel>
                <Select
                  value={filters.orderStatus}
                  onChange={(e) => setFilters({...filters, orderStatus: e.target.value})}
                  label={t('reports.order_status')}
                >
                  <MenuItem value="all">{t('reports.all_statuses')}</MenuItem>
                  <MenuItem value="pending">{t('orders.status_pending')}</MenuItem>
                  <MenuItem value="confirmed">{t('orders.status_confirmed')}</MenuItem>
                  <MenuItem value="preparing">{t('orders.status_preparing')}</MenuItem>
                  <MenuItem value="ready">{t('orders.status_ready')}</MenuItem>
                  <MenuItem value="delivered">{t('orders.status_delivered')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('reports.payment_method')}</InputLabel>
                <Select
                  value={filters.paymentMethod}
                  onChange={(e) => setFilters({...filters, paymentMethod: e.target.value})}
                  label={t('reports.payment_method')}
                >
                  <MenuItem value="all">{t('reports.all_methods')}</MenuItem>
                  <MenuItem value="cash">{t('payments.cash')}</MenuItem>
                  <MenuItem value="card">{t('payments.card')}</MenuItem>
                  <MenuItem value="paypal">{t('payments.paypal')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('reports.branch')}</InputLabel>
                <Select
                  value={filters.branch}
                  onChange={(e) => setFilters({...filters, branch: e.target.value})}
                  label={t('reports.branch')}
                >
                  <MenuItem value="all">{t('reports.all_branches')}</MenuItem>
                  <MenuItem value="berlin-mitte">Berlin Mitte</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {filters.dateRange === 'custom' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('reports.start_date')}
                    value={filters.startDate}
                    onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label={t('reports.end_date')}
                    value={filters.endDate}
                    onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setFilterDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={applyFilters} 
            variant="contained"
            sx={{ backgroundColor: appTheme.theme.primary_color || '#1976D2' }}
          >
            {t('reports.apply_filters')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog 
        open={exportDialogOpen} 
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('reports.export_report')}
          </Typography>
          <IconButton onClick={() => setExportDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('reports.export_format')}</InputLabel>
                <Select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  label={t('reports.export_format')}
                >
                  <MenuItem value="pdf">PDF (Print Ready)</MenuItem>
                  <MenuItem value="html">HTML (Web View)</MenuItem>
                  <MenuItem value="csv">CSV (Spreadsheet)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('reports.report_language')}</InputLabel>
                <Select
                  value={exportLanguage}
                  onChange={(e) => setExportLanguage(e.target.value as 'en' | 'ar')}
                  label={t('reports.report_language')}
                >
                  <MenuItem value="en">🇺🇸 English</MenuItem>
                  <MenuItem value="ar">العربية</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#e8f5e8', 
                borderRadius: 1,
                border: '1px solid #4caf50'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                  🏛️ TSE Compliant Report
                </Typography>
                <Typography variant="body2" sx={{ color: '#388e3c' }}>
                  {t('reports.tse_compliance_note')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setExportDialogOpen(false)}
            sx={{
              borderRadius: '10px',
              px: 2.5,
              py: 1.5,
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={generateExport} 
            variant="contained"
            startIcon={<FileDownload />}
            sx={{ 
              backgroundColor: appTheme.theme.primary_color || '#1976D2',
              borderRadius: '10px',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                backgroundColor: appTheme.theme.primary_color || '#1565C0',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(25, 118, 210, 0.4)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {t('reports.generate_export')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModernReportsPage;